import { useEffect, useState, lazy, Suspense } from "react";
import Lenis from "lenis";
import { Navbar } from "@/components/hvm/Navbar";
import { Hero } from "@/components/hvm/Hero";
import { Marquee } from "@/components/hvm/Marquee";
import { Manifesto } from "@/components/hvm/Manifesto";
import { Standards } from "@/components/hvm/Standards";
import { ProductMatrix } from "@/components/hvm/ProductMatrix";
import { Proof } from "@/components/hvm/Proof";
import { LeadForm } from "@/components/hvm/LeadForm";
import { Faq } from "@/components/hvm/Faq";
import { Footer } from "@/components/hvm/Footer";
import { StickyCta } from "@/components/hvm/StickyCta";
import { ExitIntent } from "@/components/hvm/ExitIntent";
import { useJsonLd } from "@/hooks/useDocumentMeta";
import { PRODUCTS, CERTS, productAnchor } from "@/data/hvm";

const ChatWidget = lazy(() => import("@/components/hvm/ChatWidget").then((m) => ({ default: m.ChatWidget })));

const HOME_SCHEMA = {
  "@graph": [
    {
      "@type": "ItemList",
      name: "Access X hostile vehicle mitigation product range",
      itemListElement: PRODUCTS.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Product",
          name: p.name,
          brand: { "@type": "Brand", name: p.brand },
          description: `${p.bestFor}. Rating: ${p.rating}. Foundation: ${p.foundation}. Span: ${p.span}.`,
          url: `https://accessxhvm.co.nz/#${productAnchor(p.name)}`,
          category: "Hostile vehicle mitigation / vehicle security barrier",
        },
      })),
    },
    {
      "@type": "DefinedTermSet",
      "@id": "https://accessxhvm.co.nz/#standards",
      name: "Hostile vehicle mitigation crash-test standards",
      url: "https://accessxhvm.co.nz/#standards",
      hasDefinedTerm: CERTS.map((c) => ({
        "@type": "DefinedTerm",
        name: /^\d{4}$/.test(c.year) ? `${c.code}:${c.year}` : c.code,
        description: `${c.title}. ${c.body} Example rating: ${c.example}.`,
        url: `https://accessxhvm.co.nz${c.guide}`,
      })),
    },
  ],
};

export default function HvmPage() {
  const [threat, setThreat] = useState({ mass: 7.2, speed: 80, touched: false });
  const [chatReady, setChatReady] = useState(false);
  useJsonLd("home-schema", HOME_SCHEMA);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, anchors: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
    idle(() => setChatReady(true));
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <div data-testid="hvm-page" className="bg-[#0B0F17] text-[#F8FAFC] min-h-screen antialiased">
      <Navbar />
      <main>
        <Hero threat={threat} setThreat={setThreat} />
        <Marquee />
        <Manifesto />
        <Standards />
        <ProductMatrix />
        <Proof />
        <Faq />
        <LeadForm threat={threat} />
      </main>
      <Footer />
      <StickyCta />
      <ExitIntent threat={threat} />
      {chatReady && (
        <Suspense fallback={null}>
          <ChatWidget />
        </Suspense>
      )}
    </div>
  );
}
